import React, { useEffect, useState, useCallback } from "react";
import { getNatureEffect, getNatureStatClass } from "../../utils/natureUtils";
import ExperienceBar from "../../components/Pokemon/ExperienceBar";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Progress,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Tooltip,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem
} from "reactstrap";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import EVTraining from "../../components/Pokemon/EVTraining";
import IVTraining from "../../components/Pokemon/IVTraining";

import api from "../../api";
import "./RosterStyles.css";

// RosterActionsDropdown component for switching Pokémon slots
const RosterActionsDropdown = ({ pokemon, refreshRoster }) => {
  // Generate available slots (1-6 excluding current slot)
  const availableSlots = [];
  for (let i = 1; i <= 6; i++) {
    if (i !== pokemon.slot) {
      availableSlots.push(i);
    }
  }

  // Handle switching Pokémon slots
  const handleRosterChange = (pokemonId, targetSlot) => {
    api.post("/v1/roster/action", { pokemon_id: pokemonId, target_slot: targetSlot }, { withCredentials: true })
      .then(() => {
        refreshRoster();
      })
      .catch(() => alert("Failed to update Pokémon in your roster."));
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <UncontrolledDropdown>
        <DropdownToggle caret size="sm" color="secondary" onClick={(e) => e.stopPropagation()}>
          Actions
        </DropdownToggle>
        <DropdownMenu>
          {availableSlots.map((slot) => (
            <DropdownItem key={slot} onClick={(e) => {
              e.stopPropagation();
              handleRosterChange(pokemon.id, slot);
            }}>
              Switch to Slot {slot}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </UncontrolledDropdown>
    </div>
  );
};

export default function Roster() {
  document.title = "Roster | Pokémon Radiance";

  const getHeldItemUi = (itemId) => {
    if (itemId === 153) return { name: "EXP Share", img: "/items/expshare.png" };
    if (itemId === 60) return { name: "Leftovers", img: "/items/leftovers.png" };
    if (itemId === 61) return { name: "Lucky Egg", img: "/items/luckyegg.png" };
    return { name: "Item", img: "/items/pokeball.png" };
  };
  
  // Function to handle equipping an item
  const handleEquipItem = async (itemId) => {
    if (!selected) return;
    
    try {
      const response = await api.post("/v1/pokemon/equip-item", {
        pokemon_id: selected.id,
        item_id: itemId
      }, { withCredentials: true });
      
      setItemEquipMessage(response.data.message);
      setItemEquipModalOpen(false);
      
      // Update the selected Pokémon's item in state
      setSelectedDetails(prev => ({
        ...prev,
        item: itemId
      }));
      
      // Update the roster to reflect the item change
      setRoster(prev => prev.map(mon => 
        mon.id === selected.id ? { ...mon, item: itemId } : mon
      ));
      
    } catch (error) {
      setItemEquipMessage(error.response?.data?.detail || "Failed to equip item");
    }
  };
  
  // Function to handle unequipping an item
  const handleUnequipItem = async () => {
    if (!selected) return;
    
    try {
      const response = await api.post(`/v1/pokemon/unequip-item?pokemon_id=${selected.id}`, {}, 
        { withCredentials: true }
      );
      
      setItemEquipMessage(response.data.message);
      setItemUnequipModalOpen(false);
      
      // Update the selected Pokémon's item in state
      setSelectedDetails(prev => ({
        ...prev,
        item: null
      }));
      
      // Update the roster to reflect the item change
      setRoster(prev => prev.map(mon => 
        mon.id === selected.id ? { ...mon, item: null } : mon
      ));
      
    } catch (error) {
      setItemEquipMessage(error.response?.data?.detail || "Failed to unequip item");
    }
  };

  // Variant prefixes used for evolution display
  const VARIANT_PREFIXES = [
    "Diamond","Golden","Shiny","Ruby","Sapphire","Emerald",
    "Shadow","Cursed","Glitter","Rainbow"
  ];

  const [roster, setRoster] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedDetails, setSelectedDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [moveToReplace, setMoveToReplace] = useState(null);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [newMoveName, setNewMoveName] = useState("");

  const [filteredTMs, setFilteredTMs] = useState([]);
  const [selectedTM, setSelectedTM] = useState("");
  const [tmMessage, setTmMessage] = useState("");

  // Evolution selection state
  const [showEvolutionModal, setShowEvolutionModal] = useState(false);
  const [selectedEvolution, setSelectedEvolution] = useState(null);

  // evolution state
  const [canEvolveData, setCanEvolveData] = useState(null);
  const [evolveLoading, setEvolveLoading] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [animSpecies, setAnimSpecies] = useState(null);

  // heal currency
  const [healCurrency, setHealCurrency] = useState("coins");
  const battleCoinsSrc = `/items/battle_coin.png`;
  
  const [equippableItems, setEquippableItems] = useState([]);
  const [itemEquipModalOpen, setItemEquipModalOpen] = useState(false);
  const [itemUnequipModalOpen, setItemUnequipModalOpen] = useState(false);
  const [itemEquipMessage, setItemEquipMessage] = useState("");
  const [playerInventory, setPlayerInventory] = useState([]);
  // Inventory loading state
  const [, setInventoryLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState({});

  function getLevel(exp) {
    const lvl = Math.floor(Math.pow(exp, 1 / 3));
    return Math.pow(lvl + 1, 3) <= exp ? lvl + 1 : lvl;
  }
  const allHealed = roster.length > 0 && roster.every(p => p.current_hp >= p.max_hp);

  const loadMonAndTMs = useCallback(async mon => {
    setTmMessage("");
    setItemEquipMessage("");
    setCanEvolveData(null);

    // 1) Pokémon details
    const { data: details } = await api.get(`/v1/pokemon/${mon.id}`, { withCredentials: true });
    
    // Important: Use mon.current_hp from roster data rather than potentially outdated details
    // We only want to update the selected details, not override the roster data
    const updatedDetails = {
      ...details,
      current_hp: mon.current_hp, // Keep the HP from the roster data
      max_hp: mon.max_hp       // Keep the max HP from the roster data
    };
    
    setSelectedDetails(updatedDetails);

    // 2) TM inventory & filter
    const { data: tmInv } = await api.get("/v1/inventory/tms", { withCredentials: true });
    const { data: raw } = await api.get(`/v1/pokemon/${mon.id}/eligible-tms`, { withCredentials: true });
    const eligible = new Set((raw.eligible_tms || []).map(e => e.move_name));
    const known = new Set([details.move1, details.move2, details.move3, details.move4]
      .filter(m => m && m !== "Empty")
    );
    setFilteredTMs(tmInv.filter(tm => tm.amount > 0 && eligible.has(tm.desc) && !known.has(tm.desc)));
    
    // 3) Check equippable items
    try {
      // Use the new endpoint with pokemon ID path parameter
      const { data: itemsData } = await api.get(`/v1/pokemon/${mon.id}/check-equippable-items`, { withCredentials: true });
      setEquippableItems(itemsData.equippable_items || []);
    } catch (error) {
      console.error("Error fetching equippable items:", error);
      setEquippableItems([]);
    }

    // 3) Evolution check
    setEvolveLoading(true);
    try {
      const { data: evo } = await api.get(`/v1/pokemon/${mon.id}/can-evolve`, { withCredentials: true });
      setCanEvolveData(evo);
    } catch (err) {
      console.error("Error fetching evolution data:", err);
    } finally {
      setEvolveLoading(false);
    }
  }, []);

  // Fetch player's inventory
  const fetchPlayerInventory = useCallback(async () => {
    setInventoryLoading(true);
    try {
      const response = await api.get('/v1/inventory');
      setPlayerInventory(response.data);
    } catch (error) {
      console.error("Error fetching inventory:", error);
    } finally {
      setInventoryLoading(false);
    }
  }, []);

  // Load evolution data - used when selecting a Pokémon
  const loadEvolutionData = useCallback(async (mon) => {
    if (!mon) return;
    
    setEvolveLoading(true);
    try {
      const response = await api.get(`/v1/pokemon/${mon.id}/can-evolve`);
      setCanEvolveData(response.data);
      // Also fetch inventory when loading evolution data
      fetchPlayerInventory();
    } catch (error) {
      console.error("Error loading evolution data:", error);
    } finally {
      setEvolveLoading(false);
    }
  }, [fetchPlayerInventory]);

  // Function to refresh roster data and maintain selected Pokémon if possible
  const refreshRoster = useCallback(async () => {
    const currentSelectedId = selected?.id;
    try {
      const { data: rosterData } = await api.get("/v1/roster", { withCredentials: true });
      setRoster(rosterData);
      
      // If a Pokémon was selected, try to keep it selected after refresh
      if (currentSelectedId) {
        const updatedMon = rosterData.find(mon => mon.id === currentSelectedId);
        if (updatedMon) {
          setSelected(updatedMon);
          // Also refresh the selected details
          await loadMonAndTMs(updatedMon);
        } else {
          // If the previously selected Pokémon is no longer in the roster, clear selection
          setSelected(null);
          setSelectedDetails(null);
        }
      }
    } catch (error) {
      console.error("Error refreshing roster:", error);
    }
  }, [selected, loadMonAndTMs]);

  const handleSelect = useCallback(async mon => {
    setSelected(mon);
    // Set selected details to null immediately to avoid flashing stale data
    setSelectedDetails(null);
    await loadMonAndTMs(mon);
    // Load evolution data when selecting a Pokémon
    await loadEvolutionData(mon);
  }, [loadMonAndTMs, loadEvolutionData]);

  useEffect(() => {
    (async () => {
      try {
        const { data: rosterData } = await api.get("/v1/roster", { withCredentials: true });
        setRoster(rosterData);

        // Check equippable items
        try {
          // If we have a default Pokemon, use its ID for the equippable items check
          const defaultMon = rosterData.find(m => m.slot === 1) || rosterData[0] || null;
          if (defaultMon) {
            const { data: itemsData } = await api.get(`/v1/pokemon/${defaultMon.id}/check-equippable-items`, { withCredentials: true });
            setEquippableItems(itemsData.equippable_items || []);
          } else {
            // Fallback to the original endpoint if no Pokemon is available
            const { data: itemsData } = await api.get("/v1/pokemon/check-equippable-items", { withCredentials: true });
            setEquippableItems(itemsData.equippable_items || []);
          }
        } catch (error) {
          console.error("Error fetching equippable items:", error);
          setEquippableItems([]);
        }

        const defaultMon = rosterData.find(m => m.slot === 1) || rosterData[0] || null;
        if (defaultMon) {
          await handleSelect(defaultMon);
        }
      } catch (err) {
        console.error("Error fetching roster:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [handleSelect]);

  const handleSelectMove = (moveSlot) => {
    setMoveToReplace(moveSlot);
    setConfirmReplace(true);
  };

  const handleConfirmReplaceMove = async () => {
    if (!selected || !selectedTM || !moveToReplace) return;
    
    try {
      const { data: res } = await api.post(
        "/v1/pokemon/replace-move",
        {
          pokemon_id: selected.id,
          tm_item_id: selectedTM,
          move_slot_to_replace: moveToReplace,
        },
        { withCredentials: true }
      );
      
      // Success message and cleanup
      setTmMessage(res.message);
      await loadMonAndTMs(selected);
      setSelectedTM("");
      setMoveToReplace(null);
      setConfirmReplace(false);
      setIsReplaceModalOpen(false);
    } catch (err) {
      setTmMessage(err?.response?.data?.detail || "Failed to replace move.");
    }
  };
  
  const cancelReplaceMove = () => {
    setMoveToReplace(null);
    setConfirmReplace(false);
    setIsReplaceModalOpen(false);
  };

  const handleUseTM = async () => {
    if (!selected || !selectedTM) return;
    try {
      const { data: res } = await api.post(
        "/v1/pokemon/use-tm",
        { pokemon_id: selected.id, tm_item_id: selectedTM },
        { withCredentials: true }
      );
      setTmMessage(res.message);
      await loadMonAndTMs(selected);
      setSelectedTM("");
    } catch (err) {
      const errorDetail = err?.response?.data?.detail;
      if (errorDetail === "All move slots are full") {
        setTmMessage(""); // Clear previous messages
        
        // Find the TM name for display in the modal
        console.log("selectedTM:", selectedTM);
        console.log("filteredTMs:", filteredTMs);
        
        // Converting selectedTM to number for comparison since IDs might be different types
        // Must use tm_item_id (not id) as shown in console logs
        const tmItem = filteredTMs.find(tm => Number(tm.tm_item_id) === Number(selectedTM));
        console.log("Found TM item:", tmItem);
        
        if (tmItem) {
          console.log("Setting new move name to:", tmItem.desc);
          setNewMoveName(tmItem.desc);
        } else {
          // Fallback: try to get move name from the error message if parsing fails
          console.log("Couldn't find TM in filteredTMs, trying API call");
          
          // Make a direct API call to get the TM name
          api.get(`/v1/inventory/items/${selectedTM}`, { withCredentials: true })
            .then(response => {
              if (response.data && response.data.desc) {
                console.log("Got TM name from API:", response.data.desc);
                setNewMoveName(response.data.desc);
              }
            })
            .catch(error => console.error("Error getting TM details:", error));
        }
        
        setIsReplaceModalOpen(true);
      } else {
        setTmMessage(errorDetail || "Failed to teach move.");
      }
    }
  };

  // Define a function to execute the evolution API call and animation
  const executeEvolution = useCallback(async (evolutionToUse) => {
    if (!selected || !evolutionToUse || evolveLoading || animating) return;
    
    setShowEvolutionModal(false);
    setEvolveLoading(true);

    try {
      // Prepare evolution data with item ID if needed
      const evolutionData = { evolution_id: evolutionToUse.id };
      
      // If this is an item-based evolution, include the item ID
      if (evolutionToUse.item > 0) {
        evolutionData.item_id = evolutionToUse.item;
      }
      
      // Send the evolution request to the backend
      const { data } = await api.post(
        `/v1/pokemon/${selected.id}/evolve`,
        evolutionData,
        { withCredentials: true }
      );
      const newName = data.new_species;
      setAnimSpecies(selected.name);
      
      // Animation for evolution
      setAnimating(true);
      
      // Do a quick animation between old→new
      const frames = [selected.name, newName];
      let step = 0;

      const iv = setInterval(() => {
        setAnimSpecies(frames[step % frames.length]);
        step++;

        // Stop after 4 flips (~2 full cycles)
        if (step >= frames.length * 4) {
          clearInterval(iv);
          setAnimating(false);
          
          // Now refresh roster & re-select the mon
          api.get("/v1/roster", { withCredentials: true })
            .then(r => {
              setRoster(r.data);
              const updated = r.data.find(m => m.id === selected.id);
              if (updated) handleSelect(updated);
              
              // Also refresh inventory to update stone counts
              fetchPlayerInventory();
              setEvolveLoading(false);
            })
            .catch(err => {
              console.error("Error refreshing roster:", err);
              setEvolveLoading(false);
            });
        }
      }, 1000);
    } catch (err) {
      console.error("Evolution failed:", err);
      setEvolveLoading(false);
      toast.error(err?.response?.data?.detail || "Evolution failed");
    }
  }, [selected, evolveLoading, animating, fetchPlayerInventory, handleSelect]);

  // Perform the actual evolution with selected evolution
  const performEvolution = useCallback(() => {
    if (!selectedEvolution) return;
    executeEvolution(selectedEvolution);
  }, [selectedEvolution, executeEvolution]);
  
  // Open evolution selection modal or auto-evolve if only one option
  const handleEvolve = useCallback(() => {
    if (!selected || !canEvolveData?.can_evolve) return;
    
    // Get current level from selectedDetails
    const currentLevel = selectedDetails?.level || 0;
    
    // Check if there's only one evolution option and it's reachable
    if (canEvolveData.evolutions && canEvolveData.evolutions.length === 1) {
      const singleEvolution = canEvolveData.evolutions[0];
      const reachable = singleEvolution.level <= currentLevel;
      const hasRequiredItem = singleEvolution.item <= 0 || 
        playerInventory.some(item => item.item === singleEvolution.item && item.amount > 0);
      
      // If it's reachable and player has required item (if needed), auto-evolve
      if (reachable && hasRequiredItem) {
        setSelectedEvolution(singleEvolution);
        executeEvolution(singleEvolution);
        return;
      }
    }
    
    // Reset selection for multiple options
    setSelectedEvolution(null);
    
    // Open the modal
    setShowEvolutionModal(true);
  }, [selected, canEvolveData, selectedDetails, playerInventory, executeEvolution]);

  // Animation state
  const [animatingHeal, setAnimatingHeal] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  
  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const createHealingEffects = (oldRoster, newRoster) => {
    // Save the starting HP values
    const startValues = {};
    oldRoster.forEach(mon => {
      startValues[mon.id] = mon.current_hp;
    });
    
    // Identify which Pokémon need healing
    const healingNeeded = newRoster.filter(mon => {
      const startHp = startValues[mon.id] || 0;
      return mon.current_hp > startHp;
    });
    
    // Create healing particles for each Pokémon that needs healing
    healingNeeded.forEach(mon => {
      const monElement = document.querySelector(`#pokemon-${mon.id} img`);
      if (!monElement) return;
      
      // Create healing effect container
      const effectContainer = document.createElement('div');
      effectContainer.className = 'heal-effect-container';
      effectContainer.id = `heal-effect-${mon.id}`;
      
      // Create sparkle particles
      for (let i = 0; i < 15; i++) {
        const particle = document.createElement('div');
        particle.className = 'heal-particle';
        
        // Random positions
        const left = Math.random() * 100;
        const bottom = Math.random() * 100;
        particle.style.left = `${left}%`;
        particle.style.bottom = `${bottom}%`;
        
        // Random animation delay
        const delay = Math.random() * 0.5;
        particle.style.animationDelay = `${delay}s`;
        
        effectContainer.appendChild(particle);
      }
      
      // Get the card element and add the effect container
      const cardElement = monElement.closest('.pokedex-card');
      if (cardElement) {
        cardElement.style.position = 'relative';
        cardElement.appendChild(effectContainer);
      }
      
      // Schedule removal of the effect container
      setTimeout(() => {
        if (effectContainer && effectContainer.parentNode) {
          effectContainer.parentNode.removeChild(effectContainer);
        }
      }, 1500);
    });
  };


  const handleHeal = async () => {
    try {
      // Check if we're already animating
      if (animatingHeal) return;
      
      // First check if any Pokémon actually need healing
      const needsHealing = roster.some(pokemon => pokemon.current_hp < pokemon.max_hp);
      if (!needsHealing) {
        setTmMessage("All Pokémon are already at full health!");
        return;
      }

      // Store the pre-healing roster for effect generation
      const oldRoster = JSON.parse(JSON.stringify(roster));
      
      // Start animation state
      setAnimatingHeal(true);
      
      // Start server request first
      const healPromise = api.post(
        "/v1/pokemon/heal-party",
        { currency: healCurrency },
        { withCredentials: true }
      );
      
      // While server is processing, immediately update UI with healed Pokemon
      const healedRoster = roster.map(mon => ({
        ...mon, 
        current_hp: mon.max_hp
      }));
      
      // Update the roster display
      setRoster(healedRoster);
      
      // Update selected Pokemon if there is one
      if (selected && selectedDetails) {
        setSelectedDetails(prev => ({
          ...prev,
          current_hp: prev.max_hp
        }));
      }
      
      // Create healing particle effects
      createHealingEffects(oldRoster, healedRoster);
      
      // Wait for server response to complete
      await healPromise;
      
      // Don't fetch the roster again - just trust the healed values
      // This prevents the "going back down" issue
    } catch (err) {
      console.error("Error healing:", err);
      const msg = err?.response?.data?.detail;
      if (msg) setTmMessage(msg);
    } finally {
      // Reset animation state after effects finish
      setTimeout(() => {
        setAnimatingHeal(false);
      }, 1500);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid className="text-center py-5">
          <Spinner color="light" />
        </Container>
      </div>
    );
  }

  const currLevel = selected ? getLevel(selected.totalexp) : 0;

  return (
    <div className="page-content" style={{ background: "#1d222e", minHeight: "100vh", padding: "2rem" }}>
      <Container fluid>
        <Breadcrumbs title="Pokémon" breadcrumbItem="Roster" />
        
        {/* Move Replacement Modal */}
        <Modal 
          isOpen={isReplaceModalOpen} 
          centered 
          size="md" 
          className="move-replace-modal"
          backdrop="static"
        >
          <div className="modal-header-custom">
            <h4 className="modal-title">
              {confirmReplace ? 'Confirm Move Replacement' : 'Choose a Move to Replace'}
            </h4>
          </div>
          <ModalBody className="move-replace-body">
            {!confirmReplace ? (
              <>
                <div className="text-center mb-4">
                  <img 
                    src={`/sprites/${selected?.name}.png`} 
                    alt={selected?.name} 
                    className="pokemon-sprite"
                  />
                  <h5 className="mt-2">{selected?.nickname || selected?.name}</h5>
                  <div className="new-move-info">
                    <span className="wants-to-learn">wants to learn</span>
                    <div className="new-move">{newMoveName}</div>
                    <span className="choose-text">Choose a move to replace:</span>
                  </div>
                </div>
                
                <div className="move-list">
                  {selectedDetails && [
                    { slot: 1, name: selectedDetails.move1 },
                    { slot: 2, name: selectedDetails.move2 },
                    { slot: 3, name: selectedDetails.move3 },
                    { slot: 4, name: selectedDetails.move4 },
                  ].map((move, idx) => (
                    <Button
                      key={idx}
                      color="light"
                      className="move-button"
                      disabled={!move.name || move.name === "Empty"}
                      onClick={() => handleSelectMove(move.slot)}
                    >
                      {move.name || "Empty"}
                    </Button>
                  ))}
                </div>
                <div className="cancel-area">
                  <Button color="danger" onClick={cancelReplaceMove}>
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <div className="confirm-replace">
                <div className="confirm-message">
                  <p>Are you sure you want to replace</p>
                  <div className="move-compare">
                    <div className="old-move">{
                      selectedDetails && moveToReplace === 1 ? selectedDetails.move1 :
                      moveToReplace === 2 ? selectedDetails.move2 :
                      moveToReplace === 3 ? selectedDetails.move3 :
                      moveToReplace === 4 ? selectedDetails.move4 : ""
                    }</div>
                    <div className="arrow">➜</div>
                    <div className="new-move">{newMoveName}</div>
                  </div>
                  <p className="warning">This cannot be undone!</p>
                </div>
                
                <div className="confirm-buttons">
                  <Button color="success" onClick={handleConfirmReplaceMove}>
                    Yes, Replace Move
                  </Button>
                  <Button color="secondary" onClick={() => setConfirmReplace(false)}>
                    Go Back
                  </Button>
                </div>
              </div>
            )}
          </ModalBody>
        </Modal>

        {selected && selectedDetails && (
          <Row className="justify-content-center mb-4">
            <Col xs="12" md="8">
              <Card className="pokedex-card shadow-sm" style={{ background: "#272d39", borderRadius: "12px" }}>
                <CardBody className="text-center">
                   <img
                    src={
                      animating
                       ? `/sprites/${animSpecies}.png`
                        : `/sprites/${selected.name}.png`
                    }
                    alt={animating ? animSpecies : selected.name}
                    style={{
                      width: 160,
                      height: 160,
                      marginBottom: "1rem",
                      transition: "opacity 0.2s"
                    }}
                  />
                  <h3 style={{ color: "#fff", marginBottom: "0.5rem" }}>
                    {selected.nickname || selected.name}
                  </h3>

                  <div style={{ color: "#ccc", fontSize: "0.9rem", marginBottom: "1rem", textAlign: "left" }}>
                    <div><strong>Level:</strong> {currLevel}</div>
                    <div><strong>Exp:</strong> {selected.totalexp}</div>
                    <div><strong>Slot:</strong> {selected.slot}</div>
                    <div><strong>HP:</strong> {selectedDetails.current_hp} / {selectedDetails.max_hp}</div>
                    
                    {/* Experience Bar */}
                    <div className="mt-2 mb-2">
                      <ExperienceBar 
                        totalExp={selected.totalexp} 
                        showLevel={true} 
                        showValues={true} 
                        className="roster-exp-bar"
                      />
                    </div>
                    
                    <div className="nature-info mt-2">
                      <div><strong>Nature:</strong> <span className="nature-badge">{selectedDetails.nature || "Neutral"}</span></div>
                      <small>{getNatureEffect(selectedDetails.nature).description}</small>
                    </div>

                    <Progress
                      color={selectedDetails.current_hp <= 0 ? "danger" : 
                             selectedDetails.current_hp < selectedDetails.max_hp * 0.2 ? "danger" :
                             selectedDetails.current_hp < selectedDetails.max_hp * 0.5 ? "warning" : "success"}
                      value={selectedDetails.current_hp <= 0 ? 0 : (selectedDetails.current_hp / selectedDetails.max_hp) * 100}
                      className="my-2 hp-progress"
                      style={{
                        transition: "width 0.4s ease-in-out",
                        boxShadow: animatingHeal && selectedDetails.current_hp < selectedDetails.max_hp ? "0 0 10px #5aff5a" : "none"
                      }}
                    />

                    <div style={{ marginTop: "0.5rem" }}>
                      <strong>Moves:</strong> {selectedDetails.move1}
                      {selectedDetails.move2 !== "Empty" && `, ${selectedDetails.move2}`}
                      {selectedDetails.move3 !== "Empty" && `, ${selectedDetails.move3}`}
                      {selectedDetails.move4 !== "Empty" && `, ${selectedDetails.move4}`}
                    </div>

                    <Nav tabs className="mt-3">
                      <NavItem>
                        <NavLink
                          className={activeTab === '1' ? 'active' : ''}
                          onClick={() => toggleTab('1')}
                          style={{ cursor: 'pointer', color: activeTab === '1' ? '#fff' : '#aaa' }}
                        >
                          Actions
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === '2' ? 'active' : ''}
                          onClick={() => toggleTab('2')}
                          style={{ cursor: 'pointer', color: activeTab === '2' ? '#fff' : '#aaa' }}
                        >
                          EV Training
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTab === '3' ? 'active' : ''}
                          onClick={() => toggleTab('3')}
                          style={{ cursor: 'pointer', color: activeTab === '3' ? '#fff' : '#aaa' }}
                        >
                          Battle Stats
                        </NavLink>
                      </NavItem>
                    </Nav>
                    
                    <TabContent activeTab={activeTab} className="mt-3">
                      <TabPane tabId="1">
                        <div style={{ marginTop: "0.75rem" }}>
                          <strong>Teach TM:</strong>
                          <Row className="align-items-center mt-2">
                            <Col xs="8">
                              <Input
                                type="select"
                                value={selectedTM}
                                onChange={e => setSelectedTM(e.target.value)}
                                disabled={filteredTMs.length === 0}
                              >
                                <option value="">-- Select TM --</option>
                                {filteredTMs.map(tm => (
                                  <option key={tm.tm_item_id} value={tm.tm_item_id}>
                                    {tm.name} ({tm.desc}) ×{tm.amount}
                                  </option>
                                ))}
                              </Input>
                            </Col>
                            <Col xs="4">
                              <Button
                                color="info"
                                onClick={handleUseTM}
                                disabled={!selectedTM}
                              >
                                Use TM
                              </Button>
                            </Col>
                          </Row>
                        </div>

                        {tmMessage && (
                          <div className="mt-3">
                            <div
                              className="alert alert-info"
                              style={{ padding: "0.5rem", fontSize: "0.9rem" }}
                            >
                              {tmMessage}
                            </div>
                          </div>
                        )}

                        <div className="mt-3">
                          <Row>
                            <Col>
                              <Button
                                color="success"
                                size="sm"
                                block
                                onClick={handleHeal}
                                disabled={animatingHeal || allHealed}
                              >
                                {animatingHeal ? (
                                  <>
                                    <Spinner size="sm" /> Healing...
                                  </>
                                ) : allHealed ? (
                                  "All Healed"
                                ) : (
                                  "Heal Party"
                                )}
                              </Button>
                            </Col>
                            <Col xs="auto">
                              <div className="d-flex align-items-center">
                                <Input
                                  type="select"
                                  value={healCurrency}
                                  onChange={e => setHealCurrency(e.target.value)}
                                  style={{ width: "120px" }}
                                >
                                  <option value="coins">Coins</option>
                                  <option value="battle_coins">Battle Coins</option>
                                </Input>
                              </div>
                            </Col>
                          </Row>
                        </div>

                        {/* EVO SECTION */}
                        <div style={{ 
                          marginTop: "1.5rem", 
                          textAlign: "left",
                          backgroundColor: "rgba(0, 0, 0, 0.2)",
                          padding: "12px 15px",
                          borderRadius: "8px",
                          border: "1px solid rgba(255, 255, 255, 0.1)"
                        }}>
                          <div style={{ 
                            fontSize: "16px", 
                            fontWeight: "600", 
                            marginBottom: "8px",
                            color: "#FFD700",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                            paddingBottom: "5px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                          }}>
                            <div>
                              <i className="ri-arrow-up-circle-line me-1"></i>
                              Evolution Path
                            </div>
                            {canEvolveData && canEvolveData.can_evolve && (
                              <Button
                                color="success"
                                size="sm"
                                onClick={handleEvolve}
                                disabled={evolveLoading || animating}
                                style={{ marginLeft: "auto" }}
                              >
                                {evolveLoading ? <Spinner size="sm" /> : animating ? "Evolving…" : "Choose Evolution"}
                              </Button>
                            )}
                          </div>
                          
                          {!evolveLoading && canEvolveData && canEvolveData.evolutions && canEvolveData.evolutions.length === 0 && (
                            <div style={{ 
                              marginTop: "0.5rem", 
                              color: "#aaa",
                              fontStyle: "italic" 
                            }}>— No evolutions available</div>
                          )}
                          
                          {/* Evolution summary list */}
                          {!evolveLoading && canEvolveData && canEvolveData.evolutions && canEvolveData.evolutions.length > 0 && (
                            <div style={{ marginTop: "10px" }}>
                              <div style={{ fontSize: "0.9rem", color: "#bbb", marginBottom: "8px" }}>
                                This Pokémon can evolve into:
                              </div>
                              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                                {canEvolveData.evolutions.map((evo, i) => {
                                  const variant = VARIANT_PREFIXES.find(p => selected.name.startsWith(p)) || "";
                                  const targetName = variant + evo.into;
                                  const reachable = evo.level <= currLevel;
                                  
                                  // Get evolution stone name if applicable
                                  const getEvolutionRequirement = (evo) => {
                                    if (evo.level > 0) {
                                      return `(Level ${evo.level})`;
                                    } else if (evo.item > 0) {
                                      const stoneNames = {
                                        1: "Fire Stone",
                                        2: "Water Stone",
                                        3: "Thunder Stone",
                                        4: "Leaf Stone",
                                        5: "Moon Stone",
                                        6: "Sun Stone",
                                        7: "Shiny Stone",
                                        8: "Dusk Stone",
                                        9: "Dawn Stone",
                                        10: "Ice Stone"
                                      };
                                      return `(${stoneNames[evo.item] || "Evolution Stone"})`;
                                    }
                                    return "";
                                  };
                                  
                                  return (
                                    <div 
                                      key={i} 
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        backgroundColor: reachable ? "rgba(76, 175, 80, 0.1)" : "rgba(255, 255, 255, 0.05)",
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        border: reachable ? "1px solid rgba(76, 175, 80, 0.3)" : "1px solid rgba(255, 255, 255, 0.1)",
                                        margin: "0 4px 4px 0"
                                      }}
                                    >
                                      <img
                                        src={`/sprites/${targetName}.png`}
                                        alt={targetName}
                                        style={{ width: 24, height: 24 }}
                                        onError={(e) => {
                                          e.target.src = `/icon/${targetName}.png`;
                                          e.target.onerror = () => {
                                            e.target.src = "/icon/unknown.png";
                                            e.target.onerror = null;
                                          };
                                        }}
                                      />
                                      <div>
                                        <span style={{ fontWeight: "500" }}>{targetName}</span>
                                        <span style={{ fontSize: "0.8rem", color: "#aaa", marginLeft: "4px" }}>
                                          {getEvolutionRequirement(evo)}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {/* Only show instruction text if there are multiple evolution options */}
                              {canEvolveData.evolutions.length > 1 && (
                                <div style={{ fontSize: "0.85rem", color: "#aaa", marginTop: "8px" }}>
                                  Click "Choose Evolution" to select which form to evolve into.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Evolution Selection Modal */}
                        <Modal 
                          isOpen={showEvolutionModal} 
                          toggle={() => setShowEvolutionModal(false)} 
                          centered
                          className="evolution-modal"
                        >
                          <ModalHeader 
                            toggle={() => setShowEvolutionModal(false)} 
                            style={{ 
                              background: "#1d222e", 
                              color: "#fff",
                              borderBottom: "1px solid rgba(255, 215, 0, 0.3)" 
                            }}
                          >
                            <i className="ri-arrow-up-circle-line me-1"></i> Choose Evolution
                          </ModalHeader>
                          <ModalBody style={{ background: "#1d222e", color: "#fff" }}>
                            {canEvolveData && canEvolveData.evolutions && canEvolveData.evolutions.length > 0 ? (
                              canEvolveData.evolutions.map((evo, i) => {
                                const currentLevel = selectedDetails?.level || 0;
                                const reachable = evo.level <= currentLevel;
                                const variant = VARIANT_PREFIXES.find(p => selected?.name?.startsWith(p)) || "";
                                const targetName = variant + evo.into;
                                
                                // Skip evolutions that aren't reachable by level
                                if (evo.level > 0 && !reachable) return null;
                              
                                return (
                                  <div
                                    key={i}
                                    onClick={() => {
                                      // Only allow selection if it's a level evolution or player has the required item
                                      const hasRequiredItem = evo.item <= 0 || playerInventory.some(item => item.item === evo.item && item.amount > 0);
                                      if (hasRequiredItem) {
                                        setSelectedEvolution(evo);
                                      }
                                    }}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "12px",
                                      margin: "10px 0",
                                      padding: "12px",
                                      borderRadius: "8px",
                                      backgroundColor: selectedEvolution === evo ? "rgba(76, 175, 80, 0.2)" : "rgba(255, 255, 255, 0.05)",
                                      border: selectedEvolution === evo ? "2px solid rgba(76, 175, 80, 0.5)" : "1px solid rgba(255, 255, 255, 0.1)",
                                      cursor: evo.item > 0 && !playerInventory.some(item => item.item === evo.item && item.amount > 0) ? "not-allowed" : "pointer",
                                      opacity: evo.item > 0 && !playerInventory.some(item => item.item === evo.item && item.amount > 0) ? 0.5 : 1,
                                      transition: "all 0.2s ease"
                                    }}
                                  >
                                    <div style={{ 
                                      width: "50px", 
                                      height: "50px", 
                                      display: "flex", 
                                      alignItems: "center", 
                                      justifyContent: "center",
                                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                                      borderRadius: "50%",
                                      padding: "2px"
                                    }}>
                                      <img
                                        src={`/sprites/${targetName}.png`}
                                        alt={targetName}
                                        style={{ width: 40, height: 40 }}
                                        onError={(e) => {
                                          e.target.src = `/icon/${targetName}.png`;
                                          e.target.onerror = () => {
                                            e.target.src = "/icon/unknown.png";
                                            e.target.onerror = null;
                                          };
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <div style={{ fontWeight: "600", fontSize: "16px" }}>
                                        {targetName}
                                      </div>
                                      <div style={{ fontSize: "0.85rem", color: "#bbb" }}>
                                        {evo.level > 0 ? (
                                          <>at Level {evo.level}</>
                                        ) : evo.item > 0 ? (
                                          <>
                                            {/* Get the stone name based on item ID */}
                                            {(() => {
                                              // Use the item_name from the API if available, otherwise fall back to hardcoded names
                                              const stoneName = evo.item_name || (() => {
                                                const stoneNames = {
                                                  1: "Fire Stone",
                                                  2: "Water Stone",
                                                  3: "Thunder Stone",
                                                  4: "Leaf Stone",
                                                  5: "Moon Stone",
                                                  6: "Sun Stone",
                                                  7: "Shiny Stone",
                                                  8: "Dusk Stone",
                                                  9: "Dawn Stone",
                                                  10: "Ice Stone"
                                                };
                                                return stoneNames[evo.item] || "Evolution Stone";
                                              })();
                                              return (
                                                <>
                                                  <img 
                                                    src={evo.item_image ? evo.item_image : `/items/evolution_stone_${evo.item}.png`} 
                                                    alt="" 
                                                    style={{ width: 24, height: 24, marginRight: 5, verticalAlign: "middle" }}
                                                    onError={(e) => { e.target.style.display = "none" }}
                                                  />
                                                  {stoneName}
                                                  {playerInventory.some(item => item.item === evo.item && item.amount > 0) ? (
                                                    <span style={{ color: "#4caf50", marginLeft: 5 }}>(Available)</span>
                                                  ) : (
                                                    <span style={{ color: "#f44336", marginLeft: 5 }}>(Not Available)</span>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </>
                                        ) : evo.happy > 0 ? (
                                          <>requires High Happiness</>
                                        ) : evo.trade > 0 ? (
                                          <>requires Trading</>
                                        ) : (
                                          <>Special Evolution</>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div style={{ textAlign: "center", padding: "20px", color: "#bbb" }}>
                                <i className="ri-information-line" style={{ fontSize: "24px", marginBottom: "10px", color: "#ffd700" }}></i>
                                <p>No evolution options available at this time.</p>
                                <p style={{ fontSize: "0.9rem" }}>This Pokémon may need to reach a higher level or require a special item to evolve.</p>
                              </div>
                            )}
                          </ModalBody>
                          <div className="modal-footer text-light" style={{ background: "#1d222e", borderTop: "1px solid rgba(255, 215, 0, 0.3)" }}>
                            <Button color="secondary" onClick={() => setShowEvolutionModal(false)}>Cancel</Button>
                            <Button 
                              color="success" 
                              onClick={performEvolution}
                              disabled={!selectedEvolution || evolveLoading || animating}
                            >
                              {evolveLoading ? <><Spinner size="sm" /> Evolving...</> : "Evolve!"}
                            </Button>
                          </div>
                        </Modal>
                      </TabPane>
                      
                      <TabPane tabId="2">
                        <EVTraining 
                          pokemonId={selected.id} 
                          onEVsUpdated={(updatedData) => {
                            // Update the selected Pokemon's stats to reflect EV changes
                            if (updatedData && updatedData.new_stats) {
                              // Update selectedDetails with the new stats from candy usage
                              setSelectedDetails(prevDetails => ({
                                ...prevDetails,
                                max_hp: updatedData.new_stats.hp,
                                attack: updatedData.new_stats.atk,
                                defense: updatedData.new_stats.def,
                                special_attack: updatedData.new_stats.spa,
                                special_defense: updatedData.new_stats.spd,
                                speed: updatedData.new_stats.spe,
                                // Also update the EVs
                                ev_hp: updatedData.evs.hp,
                                ev_atk: updatedData.evs.atk,
                                ev_def: updatedData.evs.def,
                                ev_spa: updatedData.evs.spa,
                                ev_spd: updatedData.evs.spd,
                                ev_spe: updatedData.evs.spe
                              }));
                              
                              // Update roster data with new stats as well
                              setRoster(prevRoster => 
                                prevRoster.map(poke => 
                                  poke.id === selected.id ? {
                                    ...poke,
                                    max_hp: updatedData.new_stats.hp
                                  } : poke
                                )
                              );
                            }
                          }} 
                        />
                      </TabPane>
                      
                      <TabPane tabId="3">
                        <div style={{ padding: "10px 5px" }}>
                          {/* IV Training Component */}
                          <IVTraining 
                            pokemonId={selected.id}
                            onIVsUpdated={(updatedData) => {
                              // Update the selected Pokemon's stats to reflect IV changes
                              if (updatedData && updatedData.stats) {
                                // Update selectedDetails with the new stats from IV dice usage
                                setSelectedDetails(prevDetails => ({
                                  ...prevDetails,
                                  max_hp: updatedData.stats.hp,
                                  attack: updatedData.stats.atk,
                                  defense: updatedData.stats.def,
                                  special_attack: updatedData.stats.spa,
                                  special_defense: updatedData.stats.spd,
                                  speed: updatedData.stats.spe,
                                  // Also update the IVs
                                  iv_hp: updatedData.all_ivs.hp,
                                  iv_atk: updatedData.all_ivs.attack,
                                  iv_def: updatedData.all_ivs.defense,
                                  iv_spa: updatedData.all_ivs.special_attack,
                                  iv_spd: updatedData.all_ivs.special_defense,
                                  iv_spe: updatedData.all_ivs.speed
                                }));
                                
                                // Update roster data with new stats as well
                                setRoster(prevRoster => 
                                  prevRoster.map(poke => 
                                    poke.id === selected.id ? {
                                      ...poke,
                                      max_hp: updatedData.stats.hp
                                    } : poke
                                  )
                                );
                              }
                            }}
                          />
                          
                          {/* Battle Stats */}
                          <div style={{ 
                            marginBottom: "1rem", 
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            padding: "15px",
                            borderRadius: "8px",
                            border: "1px solid rgba(255, 255, 255, 0.1)"
                          }}>
                            <h5 style={{ 
                              color: "#FFD700", 
                              borderBottom: "1px solid rgba(255, 215, 0, 0.3)",
                              paddingBottom: "8px",
                              marginBottom: "12px"
                            }}>
                              <i className="ri-sword-line me-1"></i> Battle Stats
                            </h5>
                            <Row>
                              <Col xs="6" className={getNatureStatClass(selectedDetails.nature, 'hp')}>
                                HP: {selectedDetails.max_hp}
                              </Col>
                              <Col xs="6" className={getNatureStatClass(selectedDetails.nature, 'atk')}>
                                Attack: {selectedDetails.attack}
                              </Col>
                              <Col xs="6" className={getNatureStatClass(selectedDetails.nature, 'def')}>
                                Defense: {selectedDetails.defense}
                              </Col>
                              <Col xs="6" className={getNatureStatClass(selectedDetails.nature, 'spa')}>
                                Sp. Atk: {selectedDetails.special_attack}
                              </Col>
                              <Col xs="6" className={getNatureStatClass(selectedDetails.nature, 'spd')}>
                                Sp. Def: {selectedDetails.special_defense}
                              </Col>
                              <Col xs="6" className={getNatureStatClass(selectedDetails.nature, 'spe')}>
                                Speed: {selectedDetails.speed}
                              </Col>
                            </Row>
                          </div>

                          {/* IVs */}
                          <div style={{ 
                            marginBottom: "1rem", 
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            padding: "15px",
                            borderRadius: "8px",
                            border: "1px solid rgba(255, 255, 255, 0.1)"
                          }}>
                            <h5 style={{ 
                              color: "#4caf50", 
                              borderBottom: "1px solid rgba(76, 175, 80, 0.3)",
                              paddingBottom: "8px",
                              marginBottom: "12px"
                            }}>
                              <i className="ri-seedling-line me-1"></i> Individual Values (IVs)
                            </h5>
                            <Row>
                              <Col xs="6">HP: {selectedDetails.iv_hp}</Col>
                              <Col xs="6">Atk: {selectedDetails.iv_atk}</Col>
                              <Col xs="6">Def: {selectedDetails.iv_def}</Col>
                              <Col xs="6">SpA: {selectedDetails.iv_spa}</Col>
                              <Col xs="6">SpD: {selectedDetails.iv_spd}</Col>
                              <Col xs="6">Spe: {selectedDetails.iv_spe}</Col>
                            </Row>
                          </div>

                          {/* EVs */}
                          <div style={{ 
                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                            padding: "15px",
                            borderRadius: "8px",
                            border: "1px solid rgba(255, 255, 255, 0.1)"
                          }}>
                            <h5 style={{ 
                              color: "#2196f3", 
                              borderBottom: "1px solid rgba(33, 150, 243, 0.3)",
                              paddingBottom: "8px",
                              marginBottom: "12px"
                            }}>
                              <i className="ri-bar-chart-line me-1"></i> Effort Values (EVs)
                            </h5>
                            <Row>
                              <Col xs="6">HP: {selectedDetails.ev_hp || 0}</Col>
                              <Col xs="6">Atk: {selectedDetails.ev_atk || 0}</Col>
                              <Col xs="6">Def: {selectedDetails.ev_def || 0}</Col>
                              <Col xs="6">SpA: {selectedDetails.ev_spa || 0}</Col>
                              <Col xs="6">SpD: {selectedDetails.ev_spd || 0}</Col>
                              <Col xs="6">Spe: {selectedDetails.ev_spe || 0}</Col>
                            </Row>
                          </div>
                        </div>
                      </TabPane>
                    </TabContent>

                    {/* Item Section */}
                    <div style={{ marginTop: "0.75rem", textAlign: "left" }}>
                      <strong>Item:</strong> 
                      <div className="d-flex align-items-center mt-2">
                        {selectedDetails.item ? (
                          <>
                            <div 
                              id={`item-${selected.id}`}
                              className="item-icon-container"
                              style={{ 
                                position: "relative", 
                                marginRight: "10px",
                                cursor: "pointer" 
                              }}
                              onClick={() => setItemUnequipModalOpen(true)}
                            >
                              <img 
                                src={getHeldItemUi(selectedDetails.item).img}
                                alt={getHeldItemUi(selectedDetails.item).name}
                                style={{ width: "32px", height: "32px" }} 
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/items/pokeball.png";
                                }}
                              />
                            </div>
                            <Tooltip 
                              target={`item-${selected.id}`} 
                              isOpen={tooltipOpen[`item-${selected.id}`]} 
                              toggle={() => setTooltipOpen({...tooltipOpen, [`item-${selected.id}`]: !tooltipOpen[`item-${selected.id}`]})}
                            >
                              {getHeldItemUi(selectedDetails.item).name} - Click to unequip
                            </Tooltip>
                            <span>{getHeldItemUi(selectedDetails.item).name} (Equipped)</span>
                          </>
                        ) : (
                          <>
                            <Button 
                              color="secondary" 
                              size="sm" 
                              onClick={() => setItemEquipModalOpen(true)}
                            >
                              Equip Item
                            </Button>
                            <span className="ms-2">No item equipped</span>
                          </>
                        )}
                      </div>
                      {itemEquipMessage && (
                        <div className="alert alert-info mt-2" style={{ padding: "0.5rem", fontSize: "0.9rem" }}>
                          {itemEquipMessage}
                        </div>
                      )}
                    </div>

                    {/* Stats moved to Battle Stats tab */}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

 

        {/* Heal controls */}
        <Row className="justify-content-center mb-2">
          <Col xs="12" md="6" className="text-center">
            <label style={{ marginRight: "1rem", color: "#fff", cursor: "pointer" }}>
              <Input
                type="radio"
                name="healCurrency"
                value="coins"
                checked={healCurrency === "coins"}
                onChange={e => setHealCurrency(e.target.value)}
                inline
              />{" "}
              Regular <img src="/items/coin.png" alt="Coins" style={{ width: 20, verticalAlign: "middle" }} /> (100)
            </label>
            <label style={{ color: "#fff", cursor: "pointer" }}>
              <Input
                type="radio"
                name="healCurrency"
                value="battle_coins"
                checked={healCurrency === "battle_coins"}           
                onChange={e => setHealCurrency(e.target.value)}
                inline
              />{" "}
              Battle <img src={battleCoinsSrc} alt="Battle Coins" style={{ width: 20, verticalAlign: "middle" }} /> (200)
            </label>
          </Col>
        </Row>

        <Row className="justify-content-center mb-4">
          <Col xs="12" md="6" className="text-center">
            <Button
              color="warning"
              onClick={handleHeal}
              disabled={allHealed || animatingHeal}
              style={{ fontWeight: 600 }}
            >
              {animatingHeal ? (
                <>Healing...</>
              ) : (
                <>
                  Heal All Pokémon for{" "}
                  <img
                    src={healCurrency === "coins" ? "/items/coin.png" : battleCoinsSrc}
                    alt={healCurrency === "coins" ? "Coins" : "Battle Coins"}
                    style={{ width: 24, height: 24, marginLeft: 8, verticalAlign: "middle" }}
                  />
                  {healCurrency === "coins" ? 100 : 200}
                </>
              )}
            </Button>
          </Col>
        </Row>
        
        {/* Item Equip Modal (Chest style) */}
        {itemEquipModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.75)",
              backdropFilter: "blur(5px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(25, 25, 35, 0.95)",
                borderRadius: "12px",
                width: "92%",
                maxWidth: "520px",
                boxShadow:
                  "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                overflow: "hidden",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                animation: "fadeIn 0.3s ease-out",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "rgba(0, 0, 0, 0.2)",
                }}
              >
                <h5 style={{ margin: 0, color: "#fff", fontWeight: "600" }}>
                  Equip Item
                </h5>
                <button
                  onClick={() => setItemEquipModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    fontSize: "20px",
                    cursor: "pointer",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    opacity: 0.7,
                    flexShrink: 0,
                    minWidth: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => (e.target.style.opacity = "1")}
                  onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div
                style={{
                  padding: "18px 20px",
                  color: "#fff",
                }}
              >
                <div style={{ marginBottom: 10, fontWeight: 600 }}>
                  Equip Item to {selected?.nickname || selected?.name}
                </div>

                {equippableItems.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {equippableItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleEquipItem(item.id)}
                        style={{
                          width: "100%",
                          background: "rgba(255, 255, 255, 0.08)",
                          border: "1px solid rgba(255, 255, 255, 0.14)",
                          color: "#fff",
                          borderRadius: 10,
                          padding: "12px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          textAlign: "left",
                        }}
                      >
                        <img
                          src={item.image ? `/${item.image}` : getHeldItemUi(item.id).img}
                          alt={item.name}
                          style={{ width: 36, height: 36, flexShrink: 0 }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = getHeldItemUi(item.id).img;
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700 }}>
                            {item.name} (x{item.amount})
                          </div>
                          <div style={{ fontSize: "0.85rem", opacity: 0.85 }}>
                            {item.description}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: 10,
                      background: "rgba(255, 180, 0, 0.15)",
                      border: "1px solid rgba(255, 180, 0, 0.25)",
                      borderRadius: 10,
                      padding: 12,
                      color: "#ffd27a",
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    You don't have any equippable items.
                  </div>
                )}
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  justifyContent: "center",
                  background: "rgba(0, 0, 0, 0.2)",
                }}
              >
                <button
                  onClick={() => setItemEquipModalOpen(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "rgba(255, 255, 255, 0.2)";
                    e.target.style.transform = "translateY(-1px)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "rgba(255, 255, 255, 0.1)";
                    e.target.style.transform = "translateY(0)";
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Item Unequip Modal (Chest style) */}
        {itemUnequipModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.75)",
              backdropFilter: "blur(5px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(25, 25, 35, 0.95)",
                borderRadius: "12px",
                width: "92%",
                maxWidth: "500px",
                boxShadow:
                  "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
                overflow: "hidden",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                animation: "fadeIn 0.3s ease-out",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "rgba(0, 0, 0, 0.2)",
                }}
              >
                <h5 style={{ margin: 0, color: "#fff", fontWeight: "600" }}>
                  Unequip Item
                </h5>
                <button
                  onClick={() => setItemUnequipModalOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    fontSize: "20px",
                    cursor: "pointer",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    opacity: 0.7,
                    flexShrink: 0,
                    minWidth: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => (e.target.style.opacity = "1")}
                  onMouseLeave={(e) => (e.target.style.opacity = "0.7")}
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div
                style={{
                  padding: "22px 20px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  color: "#fff",
                  gap: 12,
                }}
              >
                <img
                  src={getHeldItemUi(selectedDetails?.item).img}
                  alt={getHeldItemUi(selectedDetails?.item).name}
                  style={{ width: 64, height: 64 }}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/items/pokeball.png";
                  }}
                />
                <div style={{ fontSize: "16px", fontWeight: 700 }}>
                  Unequip {getHeldItemUi(selectedDetails?.item).name}?
                </div>
                <div style={{ opacity: 0.9 }}>
                  This will remove the held item from {selected?.nickname || selected?.name} and return it to your inventory.
                </div>
              </div>

              {/* Footer */}
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                  display: "flex",
                  justifyContent: "center",
                  gap: 10,
                  background: "rgba(0, 0, 0, 0.2)",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => setItemUnequipModalOpen(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleUnequipItem()}
                  style={{
                    background: "rgba(220, 53, 69, 0.25)",
                    border: "1px solid rgba(220, 53, 69, 0.5)",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 700,
                  }}
                >
                  Unequip
                </button>
              </div>
            </div>
          </div>
        )}

        <hr style={{ borderColor: "#444", margin: "2rem 0" }} />

        {/* Roster grid */}
        <Row className="g-3">
          {roster.map(mon => {
            const isActive = selected && mon.id === selected.id;
            const hpPct = mon.current_hp <= 0 ? 0 : (mon.current_hp / mon.max_hp) * 100;
            const level = getLevel(mon.totalexp);
            
            // Determine color based on HP percentage
            let hpColor = "success";
            if (hpPct <= 0) {
              hpColor = "danger";
            } else if (hpPct < 20) {
              hpColor = "danger";
            } else if (hpPct < 50) {
              hpColor = "warning";
            }
            
            return (
              <Col key={mon.id} xs="12" sm="6" md="4" lg="3">
                <Card
                  id={`pokemon-${mon.id}`}
                  className="pokedex-card"
                  style={{
                    background: isActive ? "#1a2633" : "#272d39",
                    border: isActive ? "2px solid #666" : "1px solid transparent",
                    borderRadius: "8px",
                    cursor: "pointer",
                    opacity: mon.current_hp <= 0 ? 0.7 : 1, // Dim fainted Pokémon
                    position: "relative" // For positioning heal effects
                  }}
                  onClick={() => handleSelect(mon)}
                >
                  <CardBody className="text-center p-3">
                    <img 
                      src={`/sprites/${mon.name}.png`} 
                      alt={mon.name} 
                      style={{ 
                        width: 96, 
                        height: 96,
                        filter: mon.current_hp <= 0 ? "grayscale(100%)" : "none", // Grayscale if fainted
                        transition: "filter 0.5s ease-in-out"
                      }} 
                    />
                    <h6 style={{ margin: "0.5rem 0 0", fontWeight: 600 }}>
                      {mon.nickname || mon.name}
                    </h6>
                    <div style={{ color: "#ccc", fontSize: "0.85rem" }}>
                      Lv. {level} | HP: {mon.current_hp}/{mon.max_hp}
                      {(mon.item === 153 || mon.item === 60 || mon.item === 61) && (
                        <span style={{ marginLeft: "5px" }}>
                          <img 
                            src={getHeldItemUi(mon.item).img}
                            alt={getHeldItemUi(mon.item).name}
                            style={{ width: "16px", height: "16px", verticalAlign: "middle" }} 
                            title={getHeldItemUi(mon.item).name}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/items/pokeball.png";
                            }}
                          />
                        </span>
                      )}
                    </div>
                    <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                      <RosterActionsDropdown pokemon={mon} refreshRoster={refreshRoster} />
                    </div>
                    <Progress 
                      color={hpColor} 
                      value={hpPct} 
                      style={{ 
                        height: "6px", 
                        marginTop: "0.5rem",
                        transition: "width 0.4s ease-in-out",
                        boxShadow: animatingHeal && mon.current_hp < mon.max_hp ? "0 0 10px #5aff5a" : "none"
                      }} 
                    />
                  </CardBody>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>
    </div>
  );
}
